import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, Clock, XCircle, AlertCircle, FileText, Send } from 'lucide-react';
import { useClientPortal } from '@/hooks/useClientPortal';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface ApprovalRequestManagerProps {
  eventId: string;
  eventTitle: string;
  clientId?: string;
  approvals?: any[];
  onUpdate?: () => void;
}

export const ApprovalRequestManager: React.FC<ApprovalRequestManagerProps> = ({ 
  eventId, 
  eventTitle, 
  clientId, 
  approvals = [],
  onUpdate 
}) => {
  const { user } = useAuth();
  const { createApprovalRequest } = useClientPortal(clientId || '');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'needs_changes':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      default:
        return <Clock className="h-4 w-4 text-blue-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'needs_changes':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const handleCreateApproval = async () => {
    if (!clientId) {
      toast.error('No client selected');
      return;
    }

    setCreating(true);
    try {
      const approval = await createApprovalRequest(eventId, notes);
      
      // Send approval request email
      const { data: clientData } = await supabase
        .from('clients')
        .select('email, name')
        .eq('id', clientId)
        .single();
      
      if (clientData?.email) {
        const approvalUrl = `${window.location.origin}/portal/approval/${approval.id}`;
        
        const { error: emailError } = await supabase.functions.invoke('send-approval-request', {
          body: {
            clientEmail: clientData.email,
            clientName: clientData.name,
            eventTitle,
            plannerName: user?.user_metadata?.display_name || user?.email || 'Your Event Planner',
            approvalUrl,
            message: notes.trim() || undefined,
          },
        });

        if (emailError) {
          console.error("Error sending approval email:", emailError);
          toast.success('Approval request created (email delivery failed)');
        } else {
          toast.success('Approval request sent via email to client');
        }
      } else {
        toast.success('Approval request created (client email not found)');
      }

      setDialogOpen(false);
      setNotes('');
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to create approval request');
      console.error('Error creating approval:', error);
    } finally {
      setCreating(false);
    }
  };

  const eventApprovals = approvals.filter(approval => approval.event_id === eventId);

  return (
    <div className="space-y-4">
      {/* Existing Approvals */}
      {eventApprovals.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Approval Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {eventApprovals.map((approval) => (
              <div key={approval.id} className="flex items-start justify-between p-3 border border-border/50 rounded">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {getStatusIcon(approval.status)}
                    <Badge className={getStatusColor(approval.status)}>
                      {approval.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(approval.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {approval.notes && (
                    <p className="text-sm text-muted-foreground mt-2">{approval.notes}</p>
                  )}
                  {approval.client_notes && (
                    <div className="mt-2 p-2 bg-muted/50 rounded text-sm">
                      <strong>Client Response:</strong> {approval.client_notes}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Create New Approval Request */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" disabled={!clientId}>
            <Send className="h-4 w-4 mr-2" />
            Request Approval
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Client Approval</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded">
              <FileText className="h-4 w-4 text-primary" />
              <div>
                <p className="font-medium text-sm">{eventTitle}</p>
                <p className="text-xs text-muted-foreground">
                  Request client approval for this event
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="approval-notes">Message to Client (Optional)</Label>
              <Textarea
                id="approval-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add a message explaining what needs approval..."
                rows={4}
              />
            </div>

            <div className="flex gap-2">
              <Button 
                onClick={handleCreateApproval} 
                disabled={creating || !clientId}
                className="flex-1"
              >
                {creating ? 'Sending...' : 'Send Approval Request'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalRequestManager;
